import { useSyncExternalStore } from "react";

/**
 * The page's one preview: playing a row's sound stops any other, so two never overlap, and each
 * row can show whether it is the one playing.
 */
let current: { id: string; audio: HTMLAudioElement } | null = null;
const listeners = new Set<() => void>();

function notify() {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Plays a file at a trim in dB; the browser's volume tops out at 1, so a boost is capped there. */
export function playSoundPreview(id: string, url: string, gainDb: number) {
  stopSoundPreview();
  const audio = new Audio(url);
  audio.volume = Math.min(1, 10 ** (gainDb / 20));
  current = { id, audio };
  const finish = () => {
    if (current?.audio !== audio) return;
    current = null;
    notify();
  };
  audio.addEventListener("ended", finish);
  notify();
  // A blocked or failed play is not worth a message; the row's file is still saved.
  void audio.play().catch(finish);
}

export function stopSoundPreview(id?: string) {
  if (!current || (id !== undefined && current.id !== id)) return;
  current.audio.pause();
  current = null;
  notify();
}

/** Whether the preview playing now is this one's. */
export function useSoundPreviewPlaying(id: string): boolean {
  return useSyncExternalStore(
    subscribe,
    () => current?.id === id,
    () => false,
  );
}
